import { useTranslation } from '@/i18n/useTranslation';
import { Button, Callout } from '@bill-alps/ui';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { LEDGER_AVAILABLE } from '@/lib/runtime/feature-flags';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useQuery } from 'convex/react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { PageLoader } from './page-loader';

/**
 * Guards the ledger routes only. The accounting module stays reachable — a
 * dark ledger sends the user to the bank transactions, not the dashboard.
 */
export function LedgerGate() {
  const { pathname } = useLocation();
  const { user, isLoading, canWriteModule } = useCurrentUser();
  const { t } = useTranslation(['ledger', 'common']);
  const companyId = user?.company?._id;
  const settings = useQuery(
    api.features.ledger.settings.get,
    LEDGER_AVAILABLE && companyId ? { companyId } : 'skip',
  );
  if (!LEDGER_AVAILABLE) {
    return <Navigate to="/accounting/bank" replace />;
  }

  if (isLoading || !companyId || settings === undefined) return <PageLoader />;
  if (!settings.available) return <Navigate to="/accounting/bank" replace />;
  const setupRoute =
    (pathname === '/accounting/activate' && canWriteModule('accounting')) ||
    pathname === '/accounting/chart' ||
    // Documents get their accounts ahead of the activation that posts them.
    pathname === '/accounting/to-classify';
  if (
    !setupRoute &&
    (!settings.settings ||
      settings.settings.status === 'importing' ||
      settings.settings.activatedAt === undefined)
  ) {
    if (canWriteModule('accounting'))
      return <Navigate to="/accounting/activate" replace />;
    return (
      <div className="space-y-4">
        <Callout variant="info">
          {t('ledger:activation.notActivatedReader')}
        </Callout>
        <Button asChild variant="outline">
          <Link to="/accounting/bank">{t('common:nav.bankTransactions')}</Link>
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
